import * as XLSX from "xlsx-js-style"

type ExportTablesToXlsxOptions = {
  sheetName?: string
  raw?: boolean
  forceNumbersAsText?: boolean
}

type SheetBuildResult = {
  sheet: XLSX.WorkSheet
  columnCount: number
  sheetName?: string
}

const getSheetName = (index: number, preferredName?: string) => {
  if (index === 0 && preferredName) return sanitizeSheetName(preferredName)
  return `Sheet${index + 1}`
}

export const exportTablesToXlsx = (
  root: HTMLElement | null | undefined,
  filename: string,
  options: ExportTablesToXlsxOptions = {}
) => {
  if (!root) return false

  const sheets = getReportSheets(root, options)
  if (!sheets.length) return false

  const wb = XLSX.utils.book_new()

  sheets.forEach((sheet, index) => {
    if (options.forceNumbersAsText) {
      forceNumbersAsText(sheet.sheet)
    }

    XLSX.utils.book_append_sheet(
      wb,
      sheet.sheet,
      getSheetName(index, sheet.sheetName || options.sheetName)
    )
  })

  XLSX.writeFile(wb, filename, {
    cellStyles: true,
  })

  return true
}

const getReportSheets = (
  root: HTMLElement,
  options: ExportTablesToXlsxOptions
) => {
  const exportTables = Array.from(
    root.querySelectorAll<HTMLTableElement>('[data-report-export-table="true"]')
  )

  if (exportTables.length) {
    return exportTables.map((table) => ({
      ...buildPlainTableSheet(table, options),
      sheetName: table.dataset.reportSheetName,
    }))
  }

  const antTables = getAntTableRoots(root)
  if (antTables.length) {
    return antTables
      .map(buildAntTableSheet)
      .filter(Boolean) as SheetBuildResult[]
  }

  const plainTables = root.matches("table")
    ? [root as HTMLTableElement]
    : Array.from(root.querySelectorAll("table"))

  return plainTables.map((table) => buildPlainTableSheet(table, options))
}

const buildPlainTableSheet = (
  table: HTMLTableElement,
  options: ExportTablesToXlsxOptions
): SheetBuildResult => {
  const sheet = XLSX.utils.table_to_sheet(table, {
    cellStyles: true,
    raw: Boolean(options.raw),
  })
  applyTableCellStyles(sheet, table)

  return {
    sheet,
    columnCount: table.rows?.[0]?.cells?.length || 0,
  }
}

const getAntTableRoots = (root: HTMLElement) => {
  const roots = root.matches(".ant-table-wrapper")
    ? [root]
    : Array.from(root.querySelectorAll<HTMLElement>(".ant-table-wrapper"))

  return roots.filter(
    (tableRoot) =>
      tableRoot.querySelector(".ant-table-thead") &&
      tableRoot.querySelector(".ant-table-tbody")
  )
}

const buildAntTableSheet = (
  tableRoot: HTMLElement
): SheetBuildResult | null => {
  const rows = getAntTableRows(tableRoot)

  if (!rows.length) return null

  const sheetData: any[][] = []
  const merges: XLSX.Range[] = []
  const occupied = new Set<string>()
  const cellStyles = new Map<string, any>()
  let columnCount = 0

  rows.forEach((row, rowIndex) => {
    sheetData[rowIndex] = sheetData[rowIndex] || []
    let columnIndex = 0

    getRowCells(row)
      .filter(isExportableCell)
      .forEach((cell) => {
        while (occupied.has(getPositionKey(rowIndex, columnIndex))) {
          columnIndex += 1
        }

        const colSpan = getCellSpan(cell, "colspan")
        const rowSpan = getCellSpan(cell, "rowspan")
        const value = normalizeCellText(cell.textContent || "")

        sheetData[rowIndex][columnIndex] = value
        columnCount = Math.max(columnCount, columnIndex + colSpan)

        if (colSpan > 1 || rowSpan > 1) {
          merges.push({
            s: { r: rowIndex, c: columnIndex },
            e: {
              r: rowIndex + rowSpan - 1,
              c: columnIndex + colSpan - 1,
            },
          })
        }

        for (let rowOffset = 0; rowOffset < rowSpan; rowOffset += 1) {
          for (let colOffset = 0; colOffset < colSpan; colOffset += 1) {
            occupied.add(
              getPositionKey(rowIndex + rowOffset, columnIndex + colOffset)
            )
          }
        }

        const style = getCellStyle(cell)
        if (style) {
          cellStyles.set(
            XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex }),
            style
          )
        }

        columnIndex += colSpan
      })
  })

  const sheet = XLSX.utils.aoa_to_sheet(sheetData)
  if (merges.length) sheet["!merges"] = merges
  sheet["!cols"] = Array.from({ length: columnCount }, () => ({ wch: 18 }))

  cellStyles.forEach((style, address) => {
    if (sheet[address]) {
      ;(sheet[address] as any).s = style
    }
  })

  return { sheet, columnCount }
}

const isExportableBodyRow = (row: HTMLTableRowElement) =>
  !row.classList.contains("ant-table-measure-row") &&
  !row.classList.contains("ant-table-placeholder") &&
  row.getAttribute("aria-hidden") !== "true"

const getAntTableRows = (tableRoot: HTMLElement) => [
  ...Array.from(tableRoot.querySelectorAll<HTMLElement>(".ant-table-thead tr")),
  ...getAntBodyRows(tableRoot),
]

const getAntBodyRows = (tableRoot: HTMLElement) => {
  const tableRows = Array.from(
    tableRoot.querySelectorAll<HTMLTableRowElement>(".ant-table-tbody tr")
  ).filter(isExportableBodyRow)

  if (tableRows.length) return tableRows

  return Array.from(
    tableRoot.querySelectorAll<HTMLElement>(".ant-table-tbody .ant-table-row")
  ).filter(
    (row) =>
      !row.classList.contains("ant-table-measure-row") &&
      !row.classList.contains("ant-table-placeholder") &&
      row.getAttribute("aria-hidden") !== "true"
  )
}

const getRowCells = (row: HTMLElement) => {
  if (row instanceof HTMLTableRowElement) {
    return Array.from(row.cells) as HTMLElement[]
  }

  return Array.from(
    row.querySelectorAll<HTMLElement>(":scope > .ant-table-cell")
  )
}

const getCellSpan = (cell: HTMLElement, spanType: "colspan" | "rowspan") => {
  const propertyValue =
    cell instanceof HTMLTableCellElement
      ? spanType === "colspan"
        ? cell.colSpan
        : cell.rowSpan
      : Number(
          cell.getAttribute(spanType) || cell.getAttribute(`aria-${spanType}`)
        )

  return Math.max(Number(propertyValue) || 1, 1)
}

const isExportableCell = (cell: HTMLElement) => {
  if (cell.classList.contains("ant-table-cell-scrollbar")) return false
  if (cell.getAttribute("aria-hidden") === "true") return false

  const style = window.getComputedStyle(cell)
  return style.display !== "none" && style.visibility !== "hidden"
}

const normalizeCellText = (value: string) => value.replace(/\s+/g, " ").trim()

const getPositionKey = (rowIndex: number, columnIndex: number) =>
  `${rowIndex}:${columnIndex}`

const getCellStyle = (cell: HTMLElement) => {
  const style = window.getComputedStyle(cell)
  const classStyle = getClassBasedCellStyle(cell)
  const fillColor = classStyle?.fill || cssColorToARGB(style.backgroundColor)
  const fontColor = classStyle?.font || cssColorToARGB(style.color)
  const excelStyle: any = {
    alignment: {
      vertical: "center",
      wrapText: true,
    },
  }

  if (fillColor) {
    excelStyle.fill = {
      patternType: "solid",
      fgColor: { rgb: fillColor },
    }
  }

  if (fontColor || Number(style.fontWeight) >= 600) {
    excelStyle.font = {
      ...(fontColor ? { color: { rgb: fontColor } } : {}),
      ...(Number(style.fontWeight) >= 600 ? { bold: true } : {}),
    }
  }

  return excelStyle.fill || excelStyle.font ? excelStyle : null
}

const applyTableCellStyles = (
  sheet: XLSX.WorkSheet,
  table: HTMLTableElement
) => {
  Array.from(table.rows).forEach((row, rowIndex) => {
    Array.from(row.cells).forEach((cell, columnIndex) => {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })
      if (!sheet[address]) return

      const style = getCellStyle(cell)
      if (style) {
        ;(sheet[address] as any).s = style
      }
    })
  })
}

const getClassBasedCellStyle = (cell: HTMLElement) => {
  const className = [
    cell.getAttribute("class") || "",
    ...Array.from(cell.querySelectorAll<HTMLElement>("[class]")).map(
      (element) => element.getAttribute("class") || ""
    ),
  ].join(" ")

  if (className.includes("text-red-500")) {
    return {
      fill: "FFFFE4E6",
      font: "FFDC2626",
    }
  }

  if (
    className.includes("text-success500") ||
    className.includes("text-green")
  ) {
    return {
      fill: "FFDCFCE7",
      font: "FF16A34A",
    }
  }

  return null
}

const cssColorToARGB = (value: string) => {
  if (!value || value === "transparent") return null

  const rgba = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)
  if (!rgba) return null

  const alpha = rgba[4] === undefined ? 1 : Number(rgba[4])
  if (alpha === 0) return null

  return `FF${toHex(rgba[1])}${toHex(rgba[2])}${toHex(rgba[3])}`
}

const toHex = (value: string) =>
  Number(value).toString(16).padStart(2, "0").toUpperCase()

const sanitizeSheetName = (value: string) =>
  value
    .replace(/[:\\/?*[\]]/g, " ")
    .trim()
    .slice(0, 31) || "Sheet1"

const forceNumbersAsText = (sheet: XLSX.WorkSheet) => {
  for (const cell in sheet) {
    if (cell[0] === "!") continue
    const value = (sheet as any)[cell]?.v
    if (typeof value === "number") {
      ;(sheet as any)[cell].t = "s"
    }
  }
}
